import moment from "moment";

/**
 * Calculate the number of days between two dates (inclusive)
 * @param {string} startDate - Start date in YYYY-MM-DD format
 * @param {string} endDate - End date in YYYY-MM-DD format
 * @returns {number} - Number of days between the dates (inclusive), or 0 if invalid
 */
function calculateDateRangeDays(startDate, endDate) {
  if (!startDate || !endDate) {
    return 0;
  }

  const start = moment(startDate).startOf("day");
  const end = moment(endDate).startOf("day");

  if (!start.isValid() || !end.isValid()) {
    return 0;
  }

  // Calculate inclusive date range
  const dayCount = end.diff(start, "days") + 1;

  return dayCount > 0 ? dayCount : 0;
}

/**
 * Get comprehensive validation status for a date range
 * @param {string} startDate - Start date in YYYY-MM-DD format
 * @param {string} endDate - End date in YYYY-MM-DD format (optional, defaults to today)
 * @param {number} maxDays - Maximum allowed days (default: 90)
 * @returns {Object} - Validation status object
 * @returns {boolean} returns.isValid - Whether the date range is valid
 * @returns {number} returns.dayCount - Number of days in the range
 * @returns {boolean} returns.hasStartDate - Whether start date is selected
 * @returns {boolean} returns.hasEndDate - Whether end date is selected
 * @returns {string|null} returns.errorMessage - Error message if validation fails
 * @returns {string} returns.effectiveEndDate - The actual end date used (today if not provided)
 */
export function getDateRangeValidationStatus(startDate, endDate, maxDays = 90) {
  const hasStartDate = !!startDate && moment(startDate).isValid();
  const hasEndDate = !!endDate && moment(endDate).isValid();

  // If neither date is provided, return valid (no validation needed)
  if (!hasStartDate && !hasEndDate) {
    return {
      isValid: true,
      dayCount: 0,
      hasStartDate: false,
      hasEndDate: false,
      errorMessage: null,
      effectiveEndDate: moment().format("YYYY-MM-DD"),
    };
  }

  // If only start date is provided, use today as end date
  if (hasStartDate && !hasEndDate) {
    const effectiveEndDate = moment().format("YYYY-MM-DD");
    const start = moment(startDate).startOf("day");
    const end = moment(effectiveEndDate).startOf("day");

    // Check if start date is in the future
    if (start.isAfter(end)) {
      return {
        isValid: false,
        dayCount: 0,
        hasStartDate,
        hasEndDate: false,
        errorMessage: "Start date cannot be in the future.",
        effectiveEndDate,
      };
    }

    const dayCount = calculateDateRangeDays(startDate, effectiveEndDate);

    // Check if range exceeds maximum allowed days
    if (dayCount > maxDays) {
      return {
        isValid: false,
        dayCount,
        hasStartDate,
        hasEndDate: false,
        errorMessage: `Date range cannot exceed ${maxDays} day${maxDays !== 1 ? "s" : ""}. Please select a date within the last ${maxDays} days.`,
        effectiveEndDate,
      };
    }

    return {
      isValid: true,
      dayCount,
      hasStartDate,
      hasEndDate: false,
      errorMessage: null,
      effectiveEndDate,
    };
  }

  // If only end date is provided, return valid (no validation needed)
  if (!hasStartDate && hasEndDate) {
    return {
      isValid: true,
      dayCount: 0,
      hasStartDate: false,
      hasEndDate,
      errorMessage: null,
      effectiveEndDate: endDate,
    };
  }

  // Both dates are provided - perform full validation
  const start = moment(startDate).startOf("day");
  const end = moment(endDate).startOf("day");

  // Check if end date is before start date
  if (end.isBefore(start)) {
    return {
      isValid: false,
      dayCount: 0,
      hasStartDate,
      hasEndDate,
      errorMessage: "End date cannot be earlier than start date.",
      effectiveEndDate: endDate,
    };
  }

  const dayCount = calculateDateRangeDays(startDate, endDate);

  // Check if range exceeds maximum allowed days
  if (dayCount > maxDays) {
    return {
      isValid: false,
      dayCount,
      hasStartDate,
      hasEndDate,
      errorMessage: `Date range cannot exceed ${maxDays} day${maxDays !== 1 ? "s" : ""}. Please select a date range within the last ${maxDays} days.`,
      effectiveEndDate: endDate,
    };
  }

  // Valid date range
  return {
    isValid: true,
    dayCount,
    hasStartDate,
    hasEndDate,
    errorMessage: null,
    effectiveEndDate: endDate,
  };
}
